import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { MessageService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { UserService } from "src/app/user/shared/user.service";
import { ErrorMessageService } from "./error-message.service";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private messageService = inject(MessageService);
  private errorMessageService = inject(ErrorMessageService);
  private translateService = inject(TranslateService);
  private dialogServie = inject(DialogService);
  private userService = inject(UserService);
  oidcSecurityService = inject(OidcSecurityService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (request.url.endsWith("/engine/user/login")) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((errorResponse) => {
        if (errorResponse instanceof HttpErrorResponse) {
          // https://github.com/angular/angular/issues/19888
          // When request of type Blob, the error is also in Blob instead of object of the json data
          if (
            errorResponse.error instanceof Blob &&
            errorResponse.error.type === "application/problem+json"
          ) {
            return this.handleErrorForBlobRequest(errorResponse);
          }
          return this.handleError(errorResponse);
        }
      }),
    );
  }

  handleErrorForBlobRequest(errorResponse: HttpErrorResponse): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: Event) => {
        try {
          const error = JSON.parse((e.target as any).result);
          const newErrorResponse = new HttpErrorResponse({
            error,
            headers: errorResponse.headers,
            status: errorResponse.status,
            statusText: errorResponse.statusText,
            url: errorResponse.url,
          });
          console.log(newErrorResponse);
          this.handleError(newErrorResponse).subscribe({
            next: (v) => resolve(v),
            error: (e) => reject(e),
          });
        } catch (_e) {
          reject(errorResponse);
        }
      };
      reader.onerror = (_e) => {
        reject(errorResponse);
      };
      reader.readAsText(errorResponse.error);
    });
  }

  handleError(errorResponse: HttpErrorResponse): Observable<any> {
    if (errorResponse.status === 401) {
      // if there is any opened dialog modal, then close it before navigating to login page
      this.dialogServie.dialogComponentRefMap.forEach((componentRef) => {
        componentRef.instance.close();
      });

      // token is expired, or auth server is down, or engine service is down, or login failed for some reason
      if (errorResponse.error) {
        this.messageService.add(
          this.errorMessageService.buildMessage(
            "error",
            errorResponse.error.title,
            errorResponse.error.detail,
            "",
          ),
        );
      } else {
        this.messageService.add(
          this.errorMessageService.buildMessage(
            "error",
            this.translateService.instant("Error"),
            this.translateService.instant("loadingUserProfileError"),
            "",
          ),
        );
      }
      setTimeout(() => {
        this.userService.logout();
      }, 2000);
    } else if (errorResponse.status === 400) {
      console.log(errorResponse);

      if (errorResponse.error.violations) {
        this.messageService.add(
          this.errorMessageService.buildErrorMessageWithTitleAndViolations(
            errorResponse,
          ),
        );
      } else {
        this.messageService.add(
          this.errorMessageService.buildErrorMessageWithDetail(errorResponse),
        );
      }
    } else if (errorResponse.status > 400 && errorResponse.status < 500) {
      console.log(errorResponse);
      this.messageService.add(
        this.errorMessageService.buildWarnMessageWithDetail(errorResponse),
      );
      return throwError(errorResponse);
      // 502 error code is used for errorResponse which wrap backend database errors
    } else if (errorResponse.status === 502) {
      console.log(errorResponse);
      this.messageService.add(
        this.errorMessageService.buildErrorMessageWithDetail(errorResponse),
      );
    } else if (errorResponse.status >= 500) {
      console.log(errorResponse);
      this.messageService.add(
        this.errorMessageService.buildErrorMessageWithDetail(errorResponse),
      );
    }

    return of(null);
  }
}
